/**
 * ReconnectingWebSocket
 *
 * The reconnection POLICY layer that owns a dumb {@link WebSocketTransport}.
 * Modeled on SIP.js's reconnection (UserAgent.onTransportDisconnect +
 * SessionManager.attemptReconnection), with the additions our event websocket
 * needs that SIP.js never did:
 *
 *   - urlProvider: SIP.js auths via static digest credentials, so its
 *     reconnect loop reuses a fixed URL. Our event websocket carries a JWT in
 *     the URL that expires, so the loop resolves a FRESH url before EVERY
 *     connect attempt. This is what fixes "reconnects forever with a stale,
 *     expired token after a weekend sleep."
 *   - Richer wake triggers via AppLifecycleAdapter (resume/focus/online/...),
 *     a superset of SIP.js's lone `window 'online'` listener — necessary
 *     because resume-from-sleep often fires no `online` event.
 *   - An optional liveness probe + time-jump detector to catch a "zombie"
 *     socket (readyState OPEN but TCP dead) faster than waiting for the OS to
 *     eventually fire `close`.
 *
 * The single `reconnecting` flag here is NOT the wedging flag the old
 * AutoReconnectingWebSocket had. It is safe specifically because the transport
 * guarantees every connect attempt settles (open, or close/timeout within
 * connectionTimeoutMs): the flag is true only while a timer is pending or an
 * attempt is in-flight, both of which are guaranteed to progress, so it can
 * never get stuck "true" with nothing scheduled.
 */

import { AppLifecycleAdapter } from '../adapters/app-lifecycle-adapter';
import { stringifyForLog } from './log-utils';
import { TransportState, WebSocketTransport } from './websocket-transport';

export interface ReconnectingWebSocketOptions {
  /**
   * Resolves a fresh, token-bearing URL. Called before EVERY connect attempt,
   * so each (re)connection uses current credentials. May reject; the loop
   * treats any rejection as a transient failure and retries with backoff. The
   * authority that STOPS the loop on a hard auth failure is close() (driven by
   * the token-state subscription), not this provider.
   */
  urlProvider: () => Promise<string>;
  /** Platform lifecycle signals hinting the app is active again. */
  lifecycleAdapter: AppLifecycleAdapter;

  /**
   * Extra handshake headers forwarded to the transport (e.g. User-Agent).
   * Only honored in Node/Electron-main; browsers must leave unset.
   */
  handshakeHeaders?: Record<string, string>;

  // Optional tuning (sensible defaults below).
  connectionTimeoutMs?: number;
  urlProviderTimeoutMs?: number;
  initialReconnectDelayMs?: number;
  maxReconnectDelayMs?: number;
  maxReconnectAttempts?: number;
  /** Enables the ping/pong zombie probe on app-active. Default true. */
  enableLivenessProbe?: boolean;
}

export class ReconnectingWebSocket {
  // Consumer callbacks (set by the adapter). Mirror the old event surface:
  // open -> 'connect', close -> 'disconnect', error -> 'connect_error'.
  public onOpen?: () => void;
  public onClose?: () => void;
  public onError?: (event: unknown) => void;
  public onMessage?: (data: string) => void;

  private readonly transport: WebSocketTransport;
  private readonly options: ReconnectingWebSocketOptions;

  // Intent + loop state.
  private shouldBeConnected = false;
  // True while a reconnect chain is active (timer pending or attempt in-flight).
  // See class comment: this cannot wedge thanks to the guaranteed-settle transport.
  private reconnecting = false;
  private currentDelayMs: number;
  private scheduledAttemptId?: ReturnType<typeof setTimeout>;

  // Liveness / wake detection.
  private lastPongAt = Date.now();
  private isValidating = false;
  private lastTickAt = Date.now();
  private timeJumpIntervalId?: ReturnType<typeof setInterval>;
  private lifecycleCleanup?: () => void;
  private pingTimeoutId?: ReturnType<typeof setTimeout>;
  private pongCheckIntervalId?: ReturnType<typeof setInterval>;

  // Config.
  private readonly urlProviderTimeoutMs: number;
  private readonly initialReconnectDelayMs: number;
  private readonly maxReconnectDelayMs: number;
  private readonly maxReconnectAttempts: number;
  private readonly enableLivenessProbe: boolean;
  private readonly pingTimeoutMs = 5000;
  private readonly timeJumpThresholdMs = 10_000;
  private readonly timeJumpCheckIntervalMs = 5000;
  private readonly staleMessageThresholdMs = 30_000;

  private readonly LOG_PREFIX = '[ReconnectingWebSocket]';

  constructor(options: ReconnectingWebSocketOptions) {
    this.options = options;
    this.urlProviderTimeoutMs = options.urlProviderTimeoutMs ?? 5000;
    this.initialReconnectDelayMs = options.initialReconnectDelayMs ?? 1000;
    this.maxReconnectDelayMs = options.maxReconnectDelayMs ?? 16_000;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 10_000;
    this.enableLivenessProbe = options.enableLivenessProbe ?? true;
    this.currentDelayMs = this.initialReconnectDelayMs;

    this.transport = new WebSocketTransport({
      connectionTimeoutMs: options.connectionTimeoutMs,
      handshakeHeaders: options.handshakeHeaders,
    });

    this.transport.onConnect = () => {
      this.log('Transport connected');
      this.currentDelayMs = this.initialReconnectDelayMs;
      this.reconnecting = false;
      this.lastPongAt = Date.now();
      this.onOpen?.();
    };

    this.transport.onDisconnect = (error) => {
      this.log('Transport disconnected', { unexpected: !!error });
      this.onClose?.();
      // Parity with SIP.js: only auto-reconnect when the drop was unexpected
      // (error set) AND we are supposed to be connected.
      if (error && this.shouldBeConnected) {
        this.attemptReconnection();
      }
    };

    this.transport.onError = (event) => this.onError?.(event);
    this.transport.onMessage = (data) => this.handleMessage(data);
  }

  // ========== Public API ==========

  /** Begin connecting and keep the connection alive (auto-reconnecting). */
  public start(): void {
    if (this.shouldBeConnected) {
      this.log('start() called but already started');
      return;
    }
    this.log('start()');
    this.shouldBeConnected = true;
    this.setupWakeDetection();
    this.attemptReconnection();
  }

  /** Intentionally close and stop reconnecting. */
  public close(): void {
    this.log('close()');
    this.shouldBeConnected = false;
    this.reconnecting = false;
    this.clearScheduledAttempt();
    this.teardownWakeDetection();
    this.clearPingTimers();
    this.transport.disconnect().catch(() => {
      // ignore — we're tearing down
    });
  }

  /** Send a text message (no-op with a warning if not connected). */
  public send(data: string): void {
    this.transport.send(data);
  }

  /** WebSocket-compatible readyState, derived from the transport state. */
  public get readyState(): number {
    switch (this.transport.state) {
      case TransportState.Connected:
        return WebSocket.OPEN;
      case TransportState.Connecting:
        return WebSocket.CONNECTING;
      case TransportState.Disconnecting:
        return WebSocket.CLOSING;
      default:
        return WebSocket.CLOSED;
    }
  }

  // ========== Reconnection loop (parity: SessionManager.attemptReconnection) ==========

  /**
   * Entry point for starting a reconnect chain from an external trigger
   * (initial start, transport drop, or app-active). Guarded so a second chain
   * cannot start while one is running; the internal recursion below bypasses
   * the guard to continue the same chain.
   */
  private attemptReconnection(): void {
    if (!this.shouldBeConnected) {
      return;
    }
    if (this.reconnecting) {
      this.log('attemptReconnection() skipped - chain already active');
      return;
    }
    this.reconnecting = true;
    this.currentDelayMs = this.initialReconnectDelayMs;
    this.scheduleAttempt(1);
  }

  private scheduleAttempt(attempt: number): void {
    if (!this.shouldBeConnected) {
      this.reconnecting = false;
      return;
    }
    if (attempt > this.maxReconnectAttempts) {
      this.logError('Maximum reconnection attempts reached', { attempt });
      this.reconnecting = false;
      return;
    }

    // First attempt fires immediately (parity with SIP.js); subsequent attempts
    // use a capped exponential backoff with jitter.
    const baseDelay = attempt === 1 ? 0 : this.currentDelayMs;
    const delay = baseDelay === 0 ? 0 : baseDelay + Math.random() * 1000;

    this.log('Scheduling reconnect attempt', { attempt, delayMs: Math.round(delay) });

    this.clearScheduledAttempt();
    this.scheduledAttemptId = setTimeout(() => {
      this.scheduledAttemptId = undefined;
      if (!this.shouldBeConnected) {
        this.reconnecting = false;
        return;
      }
      this.attemptConnect()
        .then(() => {
          // Success — onConnect already reset state and fired onOpen.
          this.reconnecting = false;
        })
        .catch((error) => {
          this.logWarn('Reconnect attempt failed', { attempt, error: String(error) });
          this.currentDelayMs = Math.min(this.currentDelayMs * 2, this.maxReconnectDelayMs);
          // Continue THIS chain (bypasses the entry guard intentionally).
          this.scheduleAttempt(attempt + 1);
        });
    }, delay);
  }

  /** Resolve a fresh URL (with its own timeout) and hand it to the transport. */
  private async attemptConnect(): Promise<void> {
    const url = await this.resolveUrl();
    await this.transport.connect(url);
  }

  /** Wrap urlProvider() in a timeout so a hung token refresh can't stall the loop. */
  private resolveUrl(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error('urlProvider timed out'));
        }
      }, this.urlProviderTimeoutMs);

      this.options.urlProvider().then(
        (url) => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve(url);
          }
        },
        (error) => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            reject(error instanceof Error ? error : new Error(String(error)));
          }
        }
      );
    });
  }

  private clearScheduledAttempt(): void {
    if (this.scheduledAttemptId) {
      clearTimeout(this.scheduledAttemptId);
      this.scheduledAttemptId = undefined;
    }
  }

  // ========== Wake detection (parity: SIP.js `window 'online'`, generalized) ==========

  private setupWakeDetection(): void {
    if (this.lifecycleCleanup || this.timeJumpIntervalId) {
      return; // already set up
    }
    this.lifecycleCleanup = this.options.lifecycleAdapter.onAppActive(() => this.handleAppActive());

    this.lastTickAt = Date.now();
    this.timeJumpIntervalId = setInterval(() => {
      const now = Date.now();
      const elapsed = now - this.lastTickAt;
      this.lastTickAt = now;
      if (elapsed > this.timeJumpThresholdMs) {
        this.log('Time jump detected (likely sleep)', { elapsedMs: elapsed });
        this.handleAppActive();
      }
    }, this.timeJumpCheckIntervalMs);
  }

  private teardownWakeDetection(): void {
    this.lifecycleCleanup?.();
    this.lifecycleCleanup = undefined;
    if (this.timeJumpIntervalId) {
      clearInterval(this.timeJumpIntervalId);
      this.timeJumpIntervalId = undefined;
    }
  }

  private handleAppActive(): void {
    if (!this.shouldBeConnected) {
      return;
    }
    this.log('App active', { readyState: this.readyState, reconnecting: this.reconnecting });

    if (!this.transport.isConnected()) {
      // We're down or mid-reconnect — make sure a chain is running.
      this.attemptReconnection();
      return;
    }

    // Connected: the socket may be a zombie (OPEN but dead). Probe it.
    if (this.enableLivenessProbe) {
      void this.validateConnection();
    }
  }

  // ========== Liveness probe (beyond SIP.js — catches zombies on wake) ==========

  private async validateConnection(): Promise<void> {
    if (this.isValidating || !this.transport.isConnected()) {
      return;
    }
    this.isValidating = true;
    const alive = await this.pingWithTimeout(this.pingTimeoutMs);
    this.isValidating = false;

    if (!this.shouldBeConnected) {
      return;
    }
    if (!alive) {
      this.logWarn('Liveness probe failed - connection is a zombie, forcing reconnect');
      this.forceReconnect();
    } else {
      this.log('Liveness probe succeeded');
    }
  }

  private pingWithTimeout(timeoutMs: number): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const startPong = this.lastPongAt;
      let resolved = false;

      const finish = (alive: boolean): void => {
        if (resolved) {
          return;
        }
        resolved = true;
        this.clearPingTimers();
        resolve(alive);
      };

      this.pingTimeoutId = setTimeout(() => finish(false), timeoutMs);
      this.pongCheckIntervalId = setInterval(() => {
        if (this.lastPongAt > startPong) {
          finish(true);
        }
      }, 100);

      try {
        this.transport.send(JSON.stringify({ event: 'ping', payload: {} }));
      } catch {
        finish(false);
      }
    });
  }

  private clearPingTimers(): void {
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId);
      this.pingTimeoutId = undefined;
    }
    if (this.pongCheckIntervalId) {
      clearInterval(this.pongCheckIntervalId);
      this.pongCheckIntervalId = undefined;
    }
  }

  /**
   * Tear down a (zombie) connection cleanly, then start a fresh chain. The
   * clean disconnect fires onDisconnect with NO error, so it will not
   * auto-reconnect — we drive the loop explicitly afterwards.
   */
  private forceReconnect(): void {
    this.transport
      .disconnect()
      .catch(() => {
        // ignore
      })
      .finally(() => {
        this.attemptReconnection();
      });
  }

  // ========== Message handling (parity: stale filter + pong tracking) ==========

  private handleMessage(data: string): void {
    try {
      const parsed = JSON.parse(data);
      if (parsed?.event === 'pong') {
        this.lastPongAt = Date.now();
      }
      if (parsed?.timestamp) {
        const age = Date.now() - parsed.timestamp;
        if (age > this.staleMessageThresholdMs) {
          this.logWarn('Discarding stale message', { event: parsed.event, ageMs: age });
          return;
        }
      }
    } catch {
      // Not JSON — pass through.
    }
    this.onMessage?.(data);
  }

  // ========== Logging ==========

  private log(message: string, data?: Record<string, unknown>): void {
    if (data) {
      console.log(`${this.LOG_PREFIX} ${message}`, stringifyForLog(data));
    } else {
      console.log(`${this.LOG_PREFIX} ${message}`);
    }
  }

  private logWarn(message: string, data?: Record<string, unknown>): void {
    if (data) {
      console.warn(`${this.LOG_PREFIX} ${message}`, stringifyForLog(data));
    } else {
      console.warn(`${this.LOG_PREFIX} ${message}`);
    }
  }

  private logError(message: string, data?: Record<string, unknown>): void {
    if (data) {
      console.error(`${this.LOG_PREFIX} ${message}`, stringifyForLog(data));
    } else {
      console.error(`${this.LOG_PREFIX} ${message}`);
    }
  }
}
